import React from 'react';
const { useState, useEffect, useLayoutEffect, useRef } = React;
import { IDENTITY_A, identityAssetUrl } from '../../brandIdentity.js';
import * as S from '../../brandStrokes.js';
import { coverGeometry, imageStyle } from '../../courseCover.js';

// Mi aprendizaje · portada de curso con la identidad A «Pizarra» (acordado con Ale el 04-11-2026).
// Una composición por curso, la misma en tarjetas y cabeceras, adaptada a cada tamaño:
// - A «Pizarra»: pizarra oscura; el nombre del curso a la izquierda, con subrayado turquesa; la imagen en su franja a
//   la derecha; sin imagen, tres círculos (en columna, o en fila si la portada es ancha).
// - B «Papel»: fondo claro; la imagen enmarcada como una foto pegada a la izquierda; el nombre a la derecha con
//   subrayado coral; sin imagen, el nombre a todo el ancho y una flecha a mano abajo a la derecha.
// El nombre es texto real (HTML), nunca se corta ni baja de 16 px: si no entra, la portada crece en alto. Los dibujos y
// la imagen tienen su propia zona: nunca se superponen con el texto. La imagen cubre su zona sin deformarse, con su
// encuadre (x, y, zoom); las zonas anchas muestran por defecto su parte de arriba. Sin imagen o si no carga, la
// composición sin imagen.

const C = IDENTITY_A.colors;
let fontsReady = false;
function ensureFonts() {
  if (fontsReady || typeof document === 'undefined') return;
  fontsReady = true;
  const css = ['title', 'bodyHeavy'].map(k => { const f = IDENTITY_A.fonts[k]; return `@font-face{font-family:'${f.family}';src:url('${identityAssetUrl(`${IDENTITY_A.fontDir}/${f.file}`)}') format('truetype');font-weight:${f.weight};font-display:swap}`; }).join('');
  const el = document.createElement('style'); el.setAttribute('data-oike-pizarra-fonts', ''); el.textContent = css; document.head.appendChild(el);
}

export function PizarraCourseCover({ cover, title = '', shape = 'card', image = null, rounded = 12, kicker = 'CURSO' }) {
  ensureFonts();
  const ref = useRef(null);
  const titleRef = useRef(null);
  const [w, setW] = useState(shape === 'card' ? 300 : 900);
  const [hh, setHh] = useState(0); // the real height (it grows when a long title needs it)
  const [line, setLine] = useState(null);
  const [failed, setFailed] = useState(false);
  useLayoutEffect(() => {
    const el = ref.current; if (!el) return;
    const measure = () => { const r = el.getBoundingClientRect(); setW(Math.max(160, r.width || w)); setHh(r.height || 0); };
    measure();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    ro?.observe(el);
    return () => ro?.disconnect();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { setFailed(false); }, [image]);
  const A = cover?.layout !== 'B';
  const showImage = !!(cover?.fileId && image && !failed);
  const reserveImage = !!cover?.fileId && !failed; // while it loads, its zone stays (the text does not jump)
  const g = coverGeometry({ layout: A ? 'A' : 'B', shape, w, hasImage: reserveImage, titleLength: String(title).length });
  // The underline of the last line of the title: measured where the text really wrapped
  useLayoutEffect(() => {
    const t = titleRef.current, box = ref.current; if (!t || !box) return undefined;
    const draw = () => {
      const rects = [...t.getClientRects()]; const b = box.getBoundingClientRect();
      const r = rects[rects.length - 1]; if (!r) { setLine(null); return; }
      setLine({ x1: r.left - b.left, x2: r.right - b.left, y: r.bottom - b.top - g.title * 0.08 });
    };
    draw();
    document.fonts?.ready?.then(draw).catch(() => {});
    return undefined;
  }, [w, title, g.title, reserveImage, A]);
  const textColor = A ? C.tiza : C.petroleo;
  const accent = A ? C.turquesa : C.coral;
  const bg = A ? { backgroundColor: C.carbon, backgroundImage: `url('${identityAssetUrl(IDENTITY_A.texture)}')`, backgroundSize: 'cover' } : { backgroundColor: C.papel, boxShadow: 'inset 0 0 0 1px rgba(35,48,47,.08)' };
  const frame = reserveImage && !A;
  const imgZone = reserveImage && (
    <div style={A ? { flex: `0 0 ${g.imgW}px`, alignSelf: 'stretch', overflow: 'hidden', background: 'rgba(244,241,234,.08)', position: 'relative' }
      : { flex: `0 0 ${g.imgW - g.pad * 0.4}px`, alignSelf: 'stretch', margin: g.pad * 0.7, marginRight: 0, position: 'relative', background: '#fff', padding: 4, boxShadow: '0 1px 3px rgba(0,0,0,.12)' }}>
      {/* the image fills its zone without setting its height (the text decides the height of the cover) */}
      <div style={{ position: 'absolute', inset: A ? 0 : 4, overflow: 'hidden', background: C.panel }}>
        {showImage && <img src={image} alt="" style={{ ...imageStyle(cover), position: 'absolute', inset: 0 }} onError={() => setFailed(true)} />}
      </div>
      {frame && <>
        <span aria-hidden="true" style={{ position: 'absolute', left: -10, top: -8, width: Math.max(26, g.imgW * 0.22), height: Math.max(9, g.h * 0.05), background: C.amarillo, opacity: 0.75, transform: 'rotate(-12deg)' }} />
        <span aria-hidden="true" style={{ position: 'absolute', right: -10, bottom: -6, width: Math.max(26, g.imgW * 0.22), height: Math.max(9, g.h * 0.05), background: C.turquesa, opacity: 0.7, transform: 'rotate(-10deg)' }} />
      </>}
    </div>
  );
  const textBlock = (
    <div style={{ flex: '1 1 auto', minWidth: 0, display: 'flex', flexDirection: 'column', justifyContent: 'center', padding: g.pad, paddingLeft: frame ? g.pad * 1.2 : g.pad, paddingBottom: !reserveImage && !A ? g.pad * 2.4 : g.pad, paddingRight: A && !reserveImage ? g.decorW : g.pad }}>
      <div style={{ fontFamily: `'${IDENTITY_A.fonts.bodyHeavy.family}', Arial, sans-serif`, fontWeight: 800, fontSize: g.kicker, letterSpacing: '.06em', color: A ? C.turquesa : C.coralTexto, lineHeight: 1.2, marginBottom: 4 }}>{kicker}</div>
      <div style={{ fontFamily: `'${IDENTITY_A.fonts.title.family}', 'Comic Sans MS', cursive`, fontSize: g.title, lineHeight: 1.02, color: textColor, overflowWrap: 'break-word', textWrap: 'pretty' }}>
        <span ref={titleRef} data-cover-title="">{title || 'Curso sin nombre'}</span>
      </div>
    </div>
  );
  return (
    <div ref={ref} role="img" aria-label={`Portada de ${title || 'curso'}`} style={{ position: 'relative', display: 'flex', minHeight: Math.round(g.h), borderRadius: rounded, overflow: 'hidden', ...bg }}>
      {!A && imgZone}
      {textBlock}
      {A && imgZone}
      <svg aria-hidden="true" width="100%" height="100%" style={{ position: 'absolute', inset: 0, pointerEvents: 'none', overflow: 'visible' }}>
        {line && S.underline(line.x1, line.y, line.x2, line.y - 2, S.seedOf(String(title) + (A ? 'A' : 'B')), { passes: 1 }).map((st, i) => <path key={i} d={st.d} fill="none" stroke={accent} strokeWidth={Math.max(2.5, g.title * 0.08)} strokeLinecap="round" />)}
        {A && !reserveImage && [C.turquesa, C.coral, C.amarillo].map((col, i) => {
          const H = hh || g.h;
          const cx = g.row ? w - g.pad - g.rr - (2 - i) * g.rr * 2.7 : w - g.pad - g.rr * 1.2;
          const cy = g.row ? H / 2 : H / 2 + (i - 1) * g.rr * 2.6;
          return S.loop(cx, cy, g.rr, g.rr, 300 + i, { passes: 1 }).map((st, k) => <path key={`${i}-${k}`} d={st.d} fill="none" stroke={col} strokeWidth={Math.max(2, g.rr * 0.18)} />);
        })}
      </svg>
      {!A && !reserveImage && (
        <svg aria-hidden="true" width={g.pad * 3.4} height={g.pad * 2.2} style={{ position: 'absolute', right: g.pad * 0.8, bottom: g.pad * 0.6 }} viewBox={`0 0 ${g.pad * 3.4} ${g.pad * 2.2}`}>
          {S.arrow(g.pad * 0.2, g.pad * 2, g.pad * 3.2, g.pad * 0.4, 77, { bend: -10, head: Math.max(7, g.h * 0.05) }).map((st, i) => <path key={i} d={st.d} fill="none" stroke={C.coral} strokeWidth={Math.max(2.2, g.h * 0.016)} strokeLinecap="round" />)}
        </svg>
      )}
    </div>
  );
}

// ── The image of a cover: loaded once per file through the server (with its permission), kept for the session ──
const cache = new Map();
/** The data URL of a cover's image; `load` asks the server (coach or person, each with its own permission). */
export function useCoverImage(fileId, load) {
  const [src, setSrc] = useState(() => (fileId && cache.get(fileId)?.src) || null);
  useEffect(() => {
    let on = true;
    if (!fileId || !load) { setSrc(null); return undefined; }
    if (!cache.has(fileId)) cache.set(fileId, { promise: load(fileId).then(r => r.dataUrl).catch(() => null) });
    const entry = cache.get(fileId);
    entry.promise.then(d => { entry.src = d; if (on) setSrc(d); });
    return () => { on = false; };
  }, [fileId]); // eslint-disable-line react-hooks/exhaustive-deps
  return src;
}

/**
 * A chosen or uploaded image, reduced in the browser to a cover copy (longest side 1600 px; never cropped, retouched or
 * generated). PNG with transparency stays PNG; the rest, JPEG. Returns { name, data } (base64) to upload.
 */
export async function coverCopyOf(dataUrl, name = 'imagen') {
  const img = await new Promise((resolve, reject) => { const i = new Image(); i.onload = () => resolve(i); i.onerror = () => reject(new Error('No se pudo leer la imagen.')); i.src = dataUrl; });
  const k = Math.min(1, 1600 / Math.max(img.naturalWidth, img.naturalHeight));
  const c = document.createElement('canvas'); c.width = Math.round(img.naturalWidth * k); c.height = Math.round(img.naturalHeight * k);
  const ctx = c.getContext('2d'); ctx.drawImage(img, 0, 0, c.width, c.height);
  let png = false;
  if (/^data:image\/png/.test(dataUrl)) { const d = ctx.getImageData(0, 0, Math.min(c.width, 64), Math.min(c.height, 64)).data; for (let i = 3; i < d.length; i += 4) if (d[i] < 250) { png = true; break; } }
  const out = c.toDataURL(png ? 'image/png' : 'image/jpeg', 0.86);
  const base = String(name).replace(/\.[a-z0-9]+$/i, '').slice(0, 60) || 'imagen';
  return { name: `Portada · ${base}.${png ? 'png' : 'jpg'}`, data: out.split(',')[1] };
}
